/**
 * Utility functions for Expandable Legend operations
 * 
 * These utilities replace the complex filter searching and processing logic
 * with simple, direct operations on the expandable legend data structure.
 */

import type {
  ExpandableLegend,
  ExpandableLegendItem,
  DataLayerWithExpandableLegend,
  ExpandableLegendOperationResult,
  LinkedSource
} from '@/types/ui/expandable-legend.types';
import type { DataLayer, DataLayerGraph } from '@/types/shared/data-layers.types';
import type { VerticalLine } from '@/modules/AssetsStudioPage/SensorFusion/types';

// Extended VerticalLine type for user-added markers
type ExtendedVerticalLine = VerticalLine & {
  markerType?: string;
  isHarmonic?: boolean;
  createdAt?: string;
  dateAdded?: string;
  username?: string;
  createdBy?: { userId: string; userName: string };
};

/**
 * Get expandable legend items from a data layer configuration
 */
export function getExpandableLegendItems(dataLayer: DataLayerWithExpandableLegend): ExpandableLegendItem[] {
  return dataLayer?.expandableLegend?.items || [];
}

/**
 * Get currently selected item IDs from expandable legend items
 */
export function getSelectedItemIds(items: ExpandableLegendItem[]): string[] {
  return items.filter(item => item.enabled).map(item => item.id);
}

/**
 * Get default selected item IDs from expandable legend configuration
 */
export function getDefaultSelection(expandableLegend?: ExpandableLegend): string[] {
  return expandableLegend?.defaultSelection || [];
}

/**
 * Toggle selection of a specific item
 * 
 * @param items - Current items array
 * @param itemId - ID of item to toggle
 * @param multiSelect - Whether multiple items can be selected
 * @returns Updated items array and operation result
 */
export function toggleItemSelection(
  items: ExpandableLegendItem[],
  itemId: string,
  multiSelect: boolean
): ExpandableLegendOperationResult {
  const previousSelectedIds = getSelectedItemIds(items);
  
  let updatedItems: ExpandableLegendItem[];
  
  if (multiSelect) {
    // Multi-select: toggle the clicked item
    updatedItems = items.map(item => 
      item.id === itemId 
        ? { ...item, enabled: !item.enabled }
        : item
    );
  } else {
    // Single-select: only the clicked item is selected
    updatedItems = items.map(item => ({
      ...item,
      enabled: item.id === itemId
    }));
  }
  
  const newSelectedIds = getSelectedItemIds(updatedItems);
  const changed = !arraysEqual(previousSelectedIds, newSelectedIds);
  
  return {
    items: updatedItems,
    selectedIds: newSelectedIds,
    changed
  };
}

/**
 * Reset items to default selection
 */
export function resetToDefaultSelection(
  items: ExpandableLegendItem[],
  defaultSelection: string[]
): ExpandableLegendOperationResult {
  const previousSelectedIds = getSelectedItemIds(items);
  
  const updatedItems = items.map(item => ({
    ...item,
    enabled: defaultSelection.includes(item.id)
  }));
  
  const newSelectedIds = getSelectedItemIds(updatedItems);
  const changed = !arraysEqual(previousSelectedIds, newSelectedIds);
  
  return {
    items: updatedItems,
    selectedIds: newSelectedIds,
    changed
  };
}

/**
 * Remove an item (for deletable user markers)
 */
export function removeItem(
  items: ExpandableLegendItem[],
  itemId: string
): ExpandableLegendOperationResult {
  const previousSelectedIds = getSelectedItemIds(items);
  
  const updatedItems = items.filter(item => item.id !== itemId);
  const newSelectedIds = getSelectedItemIds(updatedItems);
  const changed = !arraysEqual(previousSelectedIds, newSelectedIds) || items.length !== updatedItems.length;
  
  return {
    items: updatedItems,
    selectedIds: newSelectedIds,
    changed
  };
}

/**
 * Get items from linked sources (for user markers from other data layers)
 * This replaces the complex manual merging logic
 */
export function getLinkedSourceItems(
  expandableLegend: ExpandableLegend,
  allTabsData: Record<string, any>
): ExpandableLegendItem[] {
  const linkedItems: ExpandableLegendItem[] = [];
  
  expandableLegend.linkedSources.forEach((source: LinkedSource) => {
    if (!source.shareUserMarkers) return;
    
    const [tabId, dataLayerId, graphId] = source.sourceKey.split('.');
    const sourceData = allTabsData[tabId]?.dataLayers?.find((dl: DataLayer) => dl.id === dataLayerId);
    const graphData = sourceData?.graphs?.find((g: DataLayerGraph) => g.id === graphId);
    
    if (graphData?.verticalLines) {
      const userMarkers = graphData.verticalLines
        .filter((line: ExtendedVerticalLine) => line.markerType === 'user' && !line.isHarmonic)
        .map((line: ExtendedVerticalLine): ExpandableLegendItem => ({
          id: line.id,
          value: line.timestamp instanceof Date ? line.timestamp.toISOString() : String(line.timestamp),
          label: line.label || `User Marker ${line.id}`,
          enabled: false, // Not selected by default
          source: 'user',
          color: line.color || expandableLegend.defaultColor,
          deletable: true,
          createdAt: line.createdAt || new Date().toISOString(),
          markerSource: source.sourceKey,
          markerId: line.id,
          createdBy: line.createdBy
        }));
      
      linkedItems.push(...userMarkers);
    }
  });
  
  return linkedItems;
}

/**
 * Combine base items with linked source items
 */
export function getCombinedItems(
  expandableLegend: ExpandableLegend,
  allTabsData?: Record<string, any>
): ExpandableLegendItem[] {
  const baseItems = expandableLegend.items || [];
  
  if (!allTabsData || expandableLegend.linkedSources.length === 0) {
    return baseItems;
  }
  
  const linkedItems = getLinkedSourceItems(expandableLegend, allTabsData);
  
  // Combine and deduplicate by ID
  const itemsMap = new Map<string, ExpandableLegendItem>();
  
  // Add base items first (they have priority)
  baseItems.forEach((item: ExpandableLegendItem) => itemsMap.set(item.id, item));
  
  // Add linked items if they don't already exist
  linkedItems.forEach(item => {
    if (!itemsMap.has(item.id)) {
      itemsMap.set(item.id, item);
    }
  });
  
  return Array.from(itemsMap.values()).sort((a, b) => {
    // Sort by: system items first, then by creation date
    if (a.source !== b.source) {
      return a.source === 'system' ? -1 : 1;
    }
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
}

/**
 * Convert expandable legend items to the format expected by the popover component
 */
export function convertItemsToPopoverFormat(
  items: ExpandableLegendItem[],
  selectedIds: Set<string>
): any[] {
  return items.map(item => ({
    id: item.id,
    label: item.label,
    color: item.color,
    isSelected: selectedIds.has(item.id),
    isFocused: false, // Not used in new structure
    timestamps: [], // Not used in new structure
    badge: item.source,
    source: item.source,
    canDelete: item.deletable,
    markerId: item.markerId,
    markerSource: item.markerSource,
    onDelete: item.deletable && item.markerId ? () => item.markerId : undefined
  }));
}

/**
 * Check if expandable legend is enabled for a data layer
 */
export function isExpandableLegendEnabled(dataLayer?: DataLayerWithExpandableLegend): boolean {
  return dataLayer?.expandableLegend?.enabled || false;
}

/**
 * Get the timestamp filter ID (for backward compatibility during migration)
 * This can be removed after full migration
 */
export function getTimestampFilterId(dataLayer?: DataLayerWithExpandableLegend): string | null {
  if (!dataLayer?.expandableLegend?.enabled) return null;
  
  // Look for expandable_legend type filter
  const timestampFilter = dataLayer.filters?.find((f) => f.type === 'expandable_legend');
  return timestampFilter?.id || null;
}

/**
 * Create selected timestamps set from item IDs
 * Returns a Set of item IDs for matching with vertical line IDs
 */
export function createSelectedTimestampsSet(
  _items: ExpandableLegendItem[],
  selectedIds: string[]
): Set<string> {
  // Return the selected IDs directly - these match the vertical line IDs
  return new Set(selectedIds);
}

/**
 * Helper function to compare arrays for equality
 */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
}

/**
 * Debug helper to log expandable legend state
 */
export function debugExpandableLegend(
  expandableLegend?: ExpandableLegend,
  selectedIds?: Set<string>
): void {
  if (process.env.NODE_ENV !== 'development') return;

  console.group('🔍 Expandable Legend Debug');
  console.log('Configuration:', expandableLegend);
  console.log('Selected IDs:', selectedIds ? Array.from(selectedIds) : 'None');

  if (expandableLegend?.items) {
    console.table(
      expandableLegend.items.map((item: ExpandableLegendItem) => ({
        ID: item.id,
        Label: item.label,
        Enabled: item.enabled,
        Source: item.source,
        Deletable: item.deletable
      }))
    );
  }

  console.groupEnd();
}

/**
 * VerticalLine type for chart rendering (must match the type in DataLayers/types.ts)
 */
interface VerticalLineFromExpandableLegend {
  id: string;
  timestamp: Date | number;
  label: string;
  values: Record<string, number>;
  color: string;
  markerType?: 'system' | 'user';
  source?: string;
  isCustomLine?: boolean;
  _systemGroupId?: string;
  _systemGroupLabel?: string;
  _systemLineType?: string;
  createdAt?: Date | string;
  createdBy?: string;
  dateAdded?: string;
  username?: string;
  /** Target section keys from linkedSources (e.g., ['sensorFusion.sensors.vibrations']) */
  targetSections?: string[];
  /** Whether this line can be deleted (from expandable legend item) */
  deletable?: boolean;
}

/**
 * Convert expandable legend items to vertical lines for rendering on linked charts
 *
 * This function takes the expandable legend items from the backend and converts
 * them to VerticalLine format that can be rendered on Plotly charts.
 *
 * @param items - Expandable legend items from backend
 * @returns Array of VerticalLine objects ready for chart rendering
 */
export function convertExpandableLegendItemsToVerticalLines(
  items: ExpandableLegendItem[],
  targetSections?: string[]
): VerticalLineFromExpandableLegend[] {
  if (!items || items.length === 0) {
    return [];
  }

  return items
    .filter(item => item.enabled) // Only convert enabled items
    .map(item => {
      // Parse the timestamp value to Date
      const timestamp = new Date(item.value);

      return {
        id: item.id,
        timestamp,
        label: item.label,
        values: {}, // No Y values - these are timestamps from backend
        color: item.color,
        markerType: item.source === 'system' ? 'system' : 'user',
        source: item.source,
        isCustomLine: true,
        _systemGroupId: 'expandable-legend',
        _systemGroupLabel: 'Timestamps',
        _systemLineType: item.source,
        createdAt: item.createdAt,
        targetSections,
        deletable: item.deletable,
      };
    });
}

/**
 * Check if a linked source should draw vertical lines on a specific target
 *
 * @param expandableLegend - The expandable legend configuration
 * @param targetKey - The target key to check (e.g., "sensorFusion.sensors" or "sensorFusion.sensors.vibrations")
 * @returns true if vertical lines should be drawn on the target
 */
export function shouldDrawVerticalLinesOnTarget(
  expandableLegend?: ExpandableLegend,
  targetKey?: string
): boolean {
  if (!expandableLegend?.linkedSources || !targetKey) {
    return false;
  }

  return expandableLegend.linkedSources.some((source: LinkedSource) => {
    // Check if this source targets the given key and has drawVerticalLines enabled
    if (!source.drawVerticalLines) {
      return false;
    }

    // Check if the target key matches or starts with the source key
    // e.g., "sensorFusion.sensors.vibrations" matches "sensorFusion.sensors"
    return source.sourceKey === targetKey ||
           targetKey.startsWith(source.sourceKey + '.') ||
           source.sourceKey.startsWith(targetKey + '.');
  });
}

/**
 * Get vertical lines from spectrum's expandable legend for SensorFusion
 *
 * This reads the spectrum config and extracts vertical lines that should
 * be displayed on SensorFusion charts based on linkedSources configuration.
 *
 * @param spectrumConfig - The spectrum tab configuration from backend
 * @returns Array of VerticalLine objects to merge with SensorFusion's vertical lines
 */
export function getVerticalLinesFromSpectrumExpandableLegend(
  spectrumConfig?: any
): VerticalLineFromExpandableLegend[] {
  if (!spectrumConfig) {
    return [];
  }

  const allLines: VerticalLineFromExpandableLegend[] = [];

  // Helper to convert expandable legend items to vertical lines
  const extractLinesFromExpandableLegend = (expandableLegend: ExpandableLegend | undefined) => {
    if (!expandableLegend?.enabled || !expandableLegend?.items?.length) {
      return;
    }

    // Get the specific target sections from linkedSources that have drawVerticalLines enabled
    const targetSections = expandableLegend.linkedSources
      ?.filter((source: LinkedSource) => source.drawVerticalLines && source.sourceKey.startsWith('sensorFusion.'))
      ?.map((source: LinkedSource) => source.sourceKey) || [];

    if (targetSections.length > 0) {
      const lines = convertExpandableLegendItemsToVerticalLines(expandableLegend.items, targetSections);
      allLines.push(...lines);
    }
  };

  // 1. Check at root level of spectrum config
  extractLinesFromExpandableLegend(spectrumConfig.expandableLegend);

  // 2. Check in dataLayers array
  if (spectrumConfig.dataLayers) {
    spectrumConfig.dataLayers.forEach((dataLayer: DataLayerWithExpandableLegend) => {
      extractLinesFromExpandableLegend(dataLayer.expandableLegend);
    });
  }

  // 3. Check in graphs array (some configs put expandableLegend per graph)
  if (spectrumConfig.graphs) {
    spectrumConfig.graphs.forEach((graph: DataLayerGraph) => {
      extractLinesFromExpandableLegend(graph.expandableLegend);
    });
  }

  // Deduplicate by ID
  const uniqueLines = Array.from(
    new Map(allLines.map(line => [line.id, line])).values()
  );

  return uniqueLines;
}

/**
 * Filter vertical lines for a specific section based on targetSections
 *
 * @param lines - Vertical lines with targetSections info
 * @param sectionKey - The section key to filter for (e.g., 'vibrations' or 'sensorFusion.sensors.vibrations')
 * @returns Lines that should appear in this section
 */
export function filterVerticalLinesForSection(
  lines: any[],
  sectionKey: string
): any[] {
  // Normalize section key for comparison (lowercase, remove spaces)
  const normalizedSectionKey = sectionKey.toLowerCase().replace(/\s+/g, '');

  return lines.filter(line => {
    // Lines without targetSections (user-created) should appear everywhere
    if (!line.targetSections || line.targetSections.length === 0) {
      return true;
    }

    // Check if any targetSection matches this section
    return line.targetSections.some((target: string) => {
      const normalizedTarget = target.toLowerCase();

      // Exact match
      if (normalizedTarget === normalizedSectionKey) return true;

      // Target ends with the section key (e.g., 'sensorFusion.sensors.vibrations' ends with 'vibrations')
      if (normalizedTarget.endsWith('.' + normalizedSectionKey)) return true;

      // Section key ends with the target's last segment
      const targetLastSegment = normalizedTarget.split('.').pop() || '';
      if (normalizedSectionKey === targetLastSegment) return true;

      // Target is parent of section: 'sensorFusion.sensors' targets all sensors
      if (normalizedSectionKey.startsWith(normalizedTarget + '.')) return true;

      // DO NOT match if target specifies a different specific section
      // e.g., if target is 'sensorFusion.sensors.vibrations', DON'T show on 'speed'
      return false;
    });
  });
}

/**
 * Convert vertical lines from SensorFusion to expandable legend items
 *
 * This enables bidirectional sync: when user adds markers in SensorFusion,
 * they appear in Spectrum's expandable legend popover.
 *
 * @param verticalLines - Vertical lines from SensorFusion
 * @param expandableLegend - The expandable legend configuration (for default color)
 * @returns Array of ExpandableLegendItem objects to merge with backend items
 */
export function convertVerticalLinesToExpandableLegendItems(
  verticalLines: any[],
  expandableLegend?: ExpandableLegend
): ExpandableLegendItem[] {
  if (!verticalLines || verticalLines.length === 0) {
    return [];
  }

  return verticalLines
    .filter((line: ExtendedVerticalLine) => {
      // Include user-created markers and system timestamp markers (not harmonics)
      // This ensures the sensor data layer timestamps (e.g., "Dec 29, 01:53") appear in the dropdown
      if (line.isHarmonic) return false;
      // Include both user markers and system/undefined markers (timestamp markers from sensor data)
      return true;
    })
    .map((line: ExtendedVerticalLine): ExpandableLegendItem => {
      // Convert timestamp to ISO string
      let timestampValue: string;
      if (line.timestamp instanceof Date) {
        timestampValue = line.timestamp.toISOString();
      } else if (typeof line.timestamp === 'number') {
        timestampValue = new Date(line.timestamp).toISOString();
      } else {
        timestampValue = String(line.timestamp);
      }

      // Format label with date if not already formatted
      let label = line.label || `Marker ${line.id}`;
      if (!label.includes('-') && !label.includes('/')) {
        // Add date to label
        const date = new Date(timestampValue);
        const formatter = new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        label = `${line.label} - ${formatter.format(date)}`;
      }

      // Determine if this is a user marker or system marker
      const isUserMarker = line.markerType === 'user';
      const markerSource: 'user' | 'system' = isUserMarker ? 'user' : 'system';

      return {
        id: line.id,
        value: timestampValue,
        label,
        enabled: true, // Markers are enabled by default
        source: markerSource,
        color: line.color || expandableLegend?.defaultColor || '#6667dd',
        deletable: isUserMarker, // Only user markers can be deleted
        createdAt: line.createdAt || line.dateAdded || new Date().toISOString(),
        markerSource: 'sensorFusion.sensors',
        markerId: line.id,
        createdBy: line.username ? { userId: '', userName: line.username } : undefined,
      };
    });
}

/**
 * Merge backend expandable legend items with user markers from SensorFusion
 *
 * @param backendItems - Items from backend's expandable legend
 * @param sensorFusionVerticalLines - Vertical lines from SensorFusion
 * @param expandableLegend - The expandable legend configuration
 * @returns Combined and deduplicated items array
 */
export function mergeExpandableLegendItemsWithVerticalLines(
  backendItems: ExpandableLegendItem[],
  sensorFusionVerticalLines: any[],
  expandableLegend?: ExpandableLegend
): ExpandableLegendItem[] {
  // Convert SensorFusion vertical lines to expandable legend items
  const userMarkerItems = convertVerticalLinesToExpandableLegendItems(
    sensorFusionVerticalLines,
    expandableLegend
  );

  // Combine and deduplicate by ID (backend items take priority)
  const itemsMap = new Map<string, ExpandableLegendItem>();

  // Add backend items first (they have priority)
  backendItems.forEach(item => itemsMap.set(item.id, item));

  // Add user marker items if they don't already exist
  userMarkerItems.forEach(item => {
    if (!itemsMap.has(item.id)) {
      itemsMap.set(item.id, item);
    }
  });

  // Sort: system items first, then by creation date
  return Array.from(itemsMap.values()).sort((a, b) => {
    if (a.source !== b.source) {
      return a.source === 'system' ? -1 : 1;
    }
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
}